import {
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory';

import { EnumRoutes } from '@/helpers/enums/routes.enum';
import type { MyCourseCardSchema } from '@/helpers/interfaces/course-card.interface';
import type {
  CourseWithUserDataType,
  EnrolledCourseType,
} from '@/helpers/interfaces/course.interface';
import type { CourseStatisitcs } from '@/helpers/interfaces/dashboard.interface';
import type {
  LearningCategory,
  LearningCategoryWithChild,
} from '@/helpers/interfaces/learningCategories.interface';
import type { Paginated } from '@/helpers/interfaces/paginated.interface';
import type { StudentActivities } from '@/helpers/interfaces/schedule.interface';
import { axiosI } from '@/plugins';
import { usePermissionsStore } from '@/stores/permissoins.store';
import { dashboardApiHandler } from '@/submodules/postman-to-ts/apiHandler/dashboard';
import { lMSApiHandler } from '@/submodules/postman-to-ts/apiHandler/lMS';
import { organizationsApiHandler } from '@/submodules/postman-to-ts/apiHandler/organizations';
import { scheduleApiHandler } from '@/submodules/postman-to-ts/apiHandler/schedule';

const studentActivities = createQueryKeys('studentActivities', {
  getStudentActivities: (
    params: (typeof scheduleApiHandler)['student:AllActivitiesSchedule:Assignments,Exams,CoachingSessions,Events']['params'],
    userId: number
  ) => ({
    queryKey: ['studentActivities', ...Object.values(params)],
    queryFn: async () => {
      const { path, method } =
        scheduleApiHandler[
          'student:AllActivitiesSchedule:Assignments,Exams,CoachingSessions,Events'
        ];
      return (
        await axiosI.get<StudentActivities>(path({ userId }), {
          method,
          params: params,
        })
      ).data;
    },
  }),
  getStudentCourseStatistics: (userId: number) => ({
    queryKey: ['studentCourseStatistics'],
    queryFn: async () => {
      const { path, method } = dashboardApiHandler.getStudentCourseStatistics;
      return (
        await axiosI.get<CourseStatisitcs>(path({ userId }), {
          method,
        })
      ).data;
    },
  }),
  getRecommendedCourses: (
    params: typeof dashboardApiHandler.getRecommendedCourses.params
  ) => ({
    queryKey: ['recommendedCourses'],
    queryFn: async () => {
      const { path, method } = dashboardApiHandler.getRecommendedCourses;
      if (
        usePermissionsStore().checkAuthentication(
          EnumRoutes.lms.allCourses.meta
        )
      )
        return (
          await axiosI<Paginated<CourseWithUserDataType>>(path(), {
            method,
            params,
          })
        ).data.data;
      else return [];
    },
  }),
  getOrganizationLearningCategories: (
    params: typeof organizationsApiHandler.getParentLearningCategories.params
  ) => ({
    queryKey: ['organizationLearningCategories', ...Object.values(params)],
    queryFn: async () => {
      const { path, method } =
        organizationsApiHandler.getParentLearningCategories;
      return (
        await axiosI.get<Paginated<LearningCategoryWithChild>>(path(), {
          method,
          params: params,
        })
      ).data;
    },
  }),
  getEnrolledCoursesBasedOnProgress: (
    userId: string | number,
    params: typeof dashboardApiHandler.getEnrolledCoursesBasedOnProgress.params
  ) => ({
    queryKey: ['enrolledCoursesBasedOnProgress'],
    queryFn: async () => {
      const { path, method } =
        dashboardApiHandler.getEnrolledCoursesBasedOnProgress;
      return (
        await axiosI<Paginated<EnrolledCourseType>>(path({ userId }), {
          method,
          params,
        })
      ).data.data;
    },
  }),
  getAllEnrolledCourses: (
    params: typeof lMSApiHandler.courses.getAllMyCourses.params
  ) => ({
    queryKey: ['enrolledCoursesBasedOnProgress'],
    queryFn: async () => {
      const { path, method } = lMSApiHandler.courses.getAllMyCourses;
      return (
        await axiosI<Paginated<MyCourseCardSchema>>(path(), {
          method,
          params,
        })
      ).data.data;
    },
  }),
});
const learningCategories = createQueryKeys('learningCategories', {
  getOrganizationLearningCategories: (
    params: typeof organizationsApiHandler.getParentLearningCategories.params
  ) => ({
    queryKey: ['organizationLearningCategories', ...Object.values(params)],
    queryFn: async () => {
      const { path, method } =
        organizationsApiHandler.getParentLearningCategories;
      return (
        await axiosI.get<{
          count: number;
          data: LearningCategoryWithChild[];
        }>(path(), {
          method,
          params: params,
        })
      ).data;
    },
  }),
  getParentLearningCategory: (parentId?: number) => ({
    queryKey: ['organizationLearningCategories', parentId],
    queryFn: async () => {
      if (!parentId) return null;
      const { path, method } =
        organizationsApiHandler.getSingleParentLearningCategory;
      return (
        await axiosI.get<LearningCategory | null>(path({ parentId }), {
          method,
        })
      ).data;
    },
  }),
});
export const scheduleQueryKeys = mergeQueryKeys(
  studentActivities,
  learningCategories
);
